import { useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { modifyGroupedIdsSort } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import type {
  CampaignQuoteTemplateItemModel,
} from '@models/types'

import useCampaignQuoteTemplate from '@hooks/useCampaignQuoteTemplate'
import type { GenerateUrlsOptions as QuoteTemplateGenerateUrlsOptions } from '@hooks/useCampaignQuoteTemplate'

import useCampaignQuoteTemplateItems from '@hooks/useCampaignQuoteTemplateItems'
import useSelector from '@hooks/useSelector'

export const defaultRequestOptions = {
  campaignQuoteTemplate: {
    include_campaign_quote_template_items: true,
    include_campaign_quote_template_sections: true,
  },
  campaignQuoteTemplateItem: {
    include_campaign_quote_template_item_quote_total: true,
    include_campaign_quote_template_item_quote_dynamic_items: true,
  },
}

const defaultState = {
  manuallySorted: false,
  manuallyUpdatedCampaign: false,
  selectedCampaignId: undefined,
}

type SetState = (state: Partial<typeof defaultState>) => void

type RecalculateQuoteTemplatePricesParams = {
  recalculatePricesFn: ReturnType<typeof useCampaignQuoteTemplate>['callbacks']['recalculateQuoteTemplatePrices'],
  selectedCampaignId: number,
  setState: SetState,
  updateQuoteTemplateFn: ReturnType<typeof useCampaignQuoteTemplate>['callbacks']['updateCampaignQuoteTemplate'],
}

const recalculateQuoteTemplatePrices = async (params: RecalculateQuoteTemplatePricesParams) => {
  const {
    recalculatePricesFn, selectedCampaignId, setState, updateQuoteTemplateFn,
  } = params

  const updateResult = await updateQuoteTemplateFn({ campaign_id: selectedCampaignId })
  if (!updateResult.success) return updateResult

  setState({ manuallyUpdatedCampaign: false })

  return recalculatePricesFn(defaultRequestOptions.campaignQuoteTemplate)
}

type SetSelectedCampaignIdParams = {
  campaignId: number,
  setState: SetState,
}

const setSelectedCampaignId = (params: SetSelectedCampaignIdParams) => {
  const { campaignId, setState } = params

  const updatedState = {
    selectedCampaignId: campaignId,
  }

  // @ts-expect-error TS2339
  if (campaignId) updatedState.manuallyUpdatedCampaign = true

  // @ts-expect-error TS2345
  setState(updatedState)
}

type SetupSortedSectionItemIds = {
  filteredCampaignQuoteTemplateItems: CampaignQuoteTemplateItemModel[],
  setSortedSectionItemIds: VoidFunction,
}

const setupSortedSectionItemIds = (params: SetupSortedSectionItemIds) => {
  const { filteredCampaignQuoteTemplateItems, setSortedSectionItemIds } = params

  const grouped = filteredCampaignQuoteTemplateItems.reduce((acc, quoteItem) => {
    const { campaign_quote_template_section_id } = quoteItem

    const key = `Section-${campaign_quote_template_section_id}`
    // @ts-expect-error TS7053
    if (!acc[key]){
      // @ts-expect-error TS7053
      acc[key] = []
    }

    // @ts-expect-error TS7053
    acc[key].push(quoteItem.id)
    return acc
  }, {})

  // @ts-expect-error TS2554
  setSortedSectionItemIds(grouped)
}

type DragDropPayload = {
  destinationDroppableId: string,
  sourceDroppableId: string,
}

type ModifySectionItemSort = {
  dragDropPayload: DragDropPayload,
  fromIndex: number,
  setSortedSectionItemIds: Dispatch<SetStateAction<{}>>,
  setState: SetState,
  toIndex: number,
}

const modifySectionItemSort = (params: ModifySectionItemSort) => {
  const {
    dragDropPayload,
    fromIndex,
    setSortedSectionItemIds,
    setState,
    toIndex,
  } = params

  const { destinationDroppableId, sourceDroppableId } = dragDropPayload

  setSortedSectionItemIds((sortedSectionItemIds) => {
    const groupedIds = modifyGroupedIdsSort(
      fromIndex,
      toIndex,
      dragDropPayload,
      sortedSectionItemIds,
    )

    return {
      ...sortedSectionItemIds,
      [destinationDroppableId]: groupedIds[destinationDroppableId],
      [sourceDroppableId]: groupedIds[sourceDroppableId],
    }
  })

  setState({ manuallySorted: true })
}

type UseQuoteTemplateBuilderOptions = {
  campaignQuoteTemplateId: number,
  generateUrlsOptions?: QuoteTemplateGenerateUrlsOptions,
}

function useQuoteTemplateBuilder(options: UseQuoteTemplateBuilderOptions) {
  const { campaignQuoteTemplateId, generateUrlsOptions } = options

  const [state, setState] = useSetState(defaultState)
  const { manuallySorted, manuallyUpdatedCampaign, selectedCampaignId } = state

  const [sortedSectionItemIds, setSortedSectionItemIds] = useState({})

  const campaignQuoteTemplatePayload = useCampaignQuoteTemplate(
    { id: campaignQuoteTemplateId },
    {
      generateUrlsOptions,
      performHttpRequests: !!campaignQuoteTemplateId,
      requestOptions: {
        ...defaultRequestOptions.campaignQuoteTemplate,
      },
    },
  )

  const {
    callbacks: {
      recalculateQuoteTemplatePrices: recalculatePricesFn,
      updateCampaignQuoteTemplate: updateQuoteTemplateFn,
      updateCampaignQuoteTemplateSectionItemOrder,
    },
    campaignQuoteTemplate,
  } = campaignQuoteTemplatePayload

  // Set Selected Campaign
  useEffect(() => {
    if (campaignQuoteTemplate.campaign_id && !selectedCampaignId){
      // @ts-expect-error TS2322
      setState({ selectedCampaignId: campaignQuoteTemplate.campaign_id })
    }
  }, [campaignQuoteTemplate.campaign_id])

  const campaignQuoteTemplateItemsPayload = useCampaignQuoteTemplateItems({
    filters: {
      campaign_quote_template_id: campaignQuoteTemplateId,
    },
    performHttpRequests: false,
  })

  const { filteredCampaignQuoteTemplateItems } = campaignQuoteTemplateItemsPayload

  // Handle item sorting in state
  useEffect(() => {
    // @ts-expect-error TS2322
    setupSortedSectionItemIds({ filteredCampaignQuoteTemplateItems, setSortedSectionItemIds })
  }, [filteredCampaignQuoteTemplateItems.length])

  // Commit the sorted items
  useEffect(() => {
    if (manuallySorted){
      updateCampaignQuoteTemplateSectionItemOrder(sortedSectionItemIds, {
        ...defaultRequestOptions.campaignQuoteTemplate,
        ...defaultRequestOptions.campaignQuoteTemplateItem,
        include_campaign_quote_template_item_quote_total: false,
      })

      setState({ manuallySorted: false })
    }
  }, [JSON.stringify(sortedSectionItemIds), manuallySorted])

  const campaigns = useSelector(reduxState => reduxState.entities.campaigns)
  const selectedCampaign = selectedCampaignId && campaigns[selectedCampaignId] ? campaigns[selectedCampaignId] : {}

  return {
    callbacks: {
      modifySectionItemSort: (params: {
        dragDropPayload: DragDropPayload,
        fromIndex: number,
        toIndex: number,
      }) => modifySectionItemSort({
        setSortedSectionItemIds,
        setState,
        ...params,
      }),
      recalculateQuoteTemplatePrices: () => {
        recalculateQuoteTemplatePrices({
          recalculatePricesFn,
          // @ts-expect-error TS2322
          selectedCampaignId,
          setState,
          updateQuoteTemplateFn,
        })
      },
      setSelectedCampaignId: (campaignId: number) => setSelectedCampaignId({
        campaignId,
        setState,
      }),
    },
    campaignQuoteTemplatePayload,
    manuallyUpdatedCampaign,
    selectedCampaign,
    selectedCampaignId,
    sortedSectionItemIds,
  }
}

export default useQuoteTemplateBuilder
